import { Alert, Paper } from "@mui/material";
import { StyledTab, StyledTabs } from "components/Tab";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import SectionContainer from "shared/SectionContainer";
import SectionHeader from "shared/SectionHeader";
import {
  useActionWithPath,
  useUserOrganizations,
  useCurrentUsers,
} from "utils/query";
import { InfoTab } from "./Tabs/InfoTab";
import LinearProgress from "components/LinearProgress";
import { UsersTab } from "./Tabs/UsersTab";
import { InvoicesTab } from "./Tabs/InvoicesTab";
import { SubscriptionTab } from "./Tabs/SubscriptionTab";
import { PaymentsTab } from "./Tabs/PaymentsTab";
import { AccessKeysTab } from "./Tabs/AccessKeysTab";
import StyledMainButton from "components/buttons/StyledMainButton";
import AddOrganizationDialog from "components/dialogs/AddOrganizationDialog";
import {
  BillingSyncResult,
  billingSyncMessage,
} from "components/dialogs/BillingCustomerMatches";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";
import { OrganizationType } from "utils/saastypes";

const BASE_PATH = `/home/organization-setting`;

enum TABS {
  INFO = "Info",
  SUBSCRIPTIONS = "Subscriptions",
  INVOICES = "Invoices",
  PAYMENTS = "Payments",
  USERS = "Users",
  ACCESS_KEYS = "Access Keys",
}

const orgTabRoutes = {
  [TABS.INFO]: `${BASE_PATH}`,
  [TABS.SUBSCRIPTIONS]: `${BASE_PATH}/subscriptions`,
  [TABS.INVOICES]: `${BASE_PATH}/invoices`,
  [TABS.PAYMENTS]: `${BASE_PATH}/payments`,
  [TABS.USERS]: `${BASE_PATH}/users`,
  [TABS.ACCESS_KEYS]: `${BASE_PATH}/access-keys`,
};

export const OrganizationSetting = () => {
  const { pathname } = useLocation();
  const [addOrgDialogOpen, setAddOrgDialogOpen] = useState(false);
  const { authService } = useAuth();
  const [
    {
      cardInfo,
      isOrkesUser,
      currentOrganization,
      organizationType,
      isLoadingState,
      hasAdminOrRootRole,
    },
  ] = useAuthMachine(authService);

  const {
    data: currentUsers = [],
    isFetching: currentUsersFetching,
    refetch: refetchAllUsers,
  } = useCurrentUsers();

  const { refetch: refetchAllOrganizations } = useUserOrganizations();

  const toggleAddOrgDialog = () => {
    setAddOrgDialogOpen(!addOrgDialogOpen);
  };

  const [billingNotice, setBillingNotice] =
    useState<ReturnType<typeof billingSyncMessage>>(null);

  const addOrganizationAction = useActionWithPath({
    onSuccess: (response?: { billingCustomer?: BillingSyncResult }) => {
      setAddOrgDialogOpen(false);
      setBillingNotice(billingSyncMessage(response?.billingCustomer));
      refetchAllOrganizations();
    },
  });

  const addOrganization = (orgName: string, billingCustomerId?: string) => {
    if (orgName) {
      const query = new URLSearchParams({ name: orgName });
      if (billingCustomerId) {
        query.set("billingCustomerId", billingCustomerId);
      }
      // @ts-ignore
      addOrganizationAction.mutate({
        method: "post",
        path: `/registerNewUserOrganization?${query.toString()}`,
        body: "",
      });
    }
  };

  const tabLabels = useMemo(
    () => [
      { label: TABS.INFO, hidden: false, to: orgTabRoutes[TABS.INFO] },
      {
        label: TABS.SUBSCRIPTIONS,
        hidden: true,
        to: orgTabRoutes[TABS.SUBSCRIPTIONS],
      },
      { label: TABS.INVOICES, hidden: true, to: orgTabRoutes[TABS.INVOICES] },
      {
        label: TABS.PAYMENTS,
        hidden: organizationType == null || !hasAdminOrRootRole,
        to: orgTabRoutes[TABS.PAYMENTS],
      },
      {
        label: TABS.USERS,
        hidden:
          (organizationType === OrganizationType.FREE_TRIAL ||
            !hasAdminOrRootRole) &&
          !isOrkesUser,
        to: orgTabRoutes[TABS.USERS],
      },
      {
        // The endpoints are guarded by environmentManagement or globalBypass. Both COMPANY_ADMIN
        // and COMPANY_ROOT hold the former, so this mirrors the server's rule rather than
        // inventing a looser one: a plain member would only ever see a 403.
        label: TABS.ACCESS_KEYS,
        hidden: !hasAdminOrRootRole && !isOrkesUser,
        to: orgTabRoutes[TABS.ACCESS_KEYS],
      },
    ],
    [organizationType, hasAdminOrRootRole, isOrkesUser]
  );

  const filteredTabs = useMemo(
    () => tabLabels.filter((tab) => !tab.hidden),
    [tabLabels]
  );

  const selectedTabIndex = useMemo(
    () => filteredTabs.findIndex((tab) => tab.to === pathname),
    [filteredTabs, pathname]
  );

  return (
    <>
      <Helmet>
        <title>Orkes Cloud - Organization Settings</title>
      </Helmet>
      {(isLoadingState || currentUsersFetching) && <LinearProgress />}
      {addOrgDialogOpen && (
        <AddOrganizationDialog
          handleClose={toggleAddOrgDialog}
          handleAdd={addOrganization}
          progressPending={addOrganizationAction.isLoading}
        />
      )}
      <SectionHeader
        title="Organization Settings"
        actions={
          isOrkesUser ? (
            <StyledMainButton
              size="small"
              color={"inherit"}
              style={{
                width: 250,
                fontSize: 14,
                marginRight: 10,
              }}
              onClick={() => {
                toggleAddOrgDialog();
              }}
            >
              Create New Organization
            </StyledMainButton>
          ) : null
        }
      />

      <SectionContainer>
        {billingNotice && (
          <Alert
            severity={billingNotice.severity}
            onClose={() => setBillingNotice(null)}
            sx={{ marginRight: "10px", marginTop: "16px" }}
          >
            {billingNotice.text}
          </Alert>
        )}
        <Paper
          elevation={1}
          id="organization-settings-wrapper"
          sx={{
            marginRight: "10px",
            marginTop: "16px",
            minHeight: "350px",
          }}
        >
          <StyledTabs
            sx={{
              px: 10,
              "& .MuiTabs-flexContainer": {
                gap: "24px",
              },
            }}
            value={selectedTabIndex}
            variant="scrollable"
            scrollButtons="auto"
          >
            {filteredTabs.map((tab) => (
              <StyledTab
                key={tab.label}
                label={tab.label}
                component={Link}
                to={tab.to}
              />
            ))}
          </StyledTabs>

          {pathname === orgTabRoutes[TABS.INFO] && (
            <InfoTab
              organizationName={currentOrganization?.name}
              organizationId={currentOrganization?.id}
            />
          )}
          {pathname === orgTabRoutes[TABS.SUBSCRIPTIONS] && <SubscriptionTab />}
          {pathname === orgTabRoutes[TABS.INVOICES] && <InvoicesTab />}
          {pathname === orgTabRoutes[TABS.PAYMENTS] && (
            <PaymentsTab
              cardInfo={cardInfo}
              organizationType={organizationType}
            />
          )}
          {pathname === orgTabRoutes[TABS.ACCESS_KEYS] && <AccessKeysTab />}
          {pathname === orgTabRoutes[TABS.USERS] && (
            <UsersTab
              currentUsersFetching={currentUsersFetching}
              currentUsers={currentUsers}
              refetchAllUsers={refetchAllUsers}
            />
          )}
        </Paper>
      </SectionContainer>
    </>
  );
};
